import React from 'react';
import { twJoin } from 'tailwind-merge';

import type { Ryecon } from '@dbt-labs/sourdough';
import { Icon } from '@dbt-labs/sourdough';

import { useDagContext } from '../providers/DagProvider';
import {
  BoundingBox,
  ContextMenuItem,
  Position,
  useContextMenuPosition,
} from './ContextMenu';

export interface DagContextMenuOption {
  label: string;
  ryecon?: Ryecon;
  // If onSelect is not defined, the menu option is disabled
  onSelect?: () => void;
}

interface DagContextMenuItemParams extends DagContextMenuOption {
  showEmptyIcon: boolean;
}

export type ContextMenuProps = {
  // Define the box where its valid to display the context menu within
  boundingBox: BoundingBox;
  options: DagContextMenuOption[];
  position: Position;
  timestamp: number;
  rootElement?: HTMLDivElement | null | undefined;
};

const DagContextMenuItem: React.FC<DagContextMenuItemParams> = ({
  showEmptyIcon,
  label,
  ryecon,
  onSelect,
}) => {
  const isDisabled = !onSelect;
  return (
    <ContextMenuItem
      className={twJoin(
        isDisabled && 'italic text-fgAlt',
        'block w-full px-4 py-2 text-left hover:bg-bgBrandMuted',
      )}
      onSelect={onSelect}
    >
      {!ryecon && showEmptyIcon && (
        <>
          <div></div> &nbsp;
        </>
      )}
      {ryecon && (
        <>
          (
          <Icon className="select-none" ryecon={ryecon} size="sm" />) &nbsp;
        </>
      )}
      {label}
    </ContextMenuItem>
  );
};

export const DagContextMenu: React.FC<ContextMenuProps> = ({
  rootElement,
  options: inputOptions,
  timestamp,
  position,
  boundingBox,
}) => {
  const menuContainerRef = React.useRef<HTMLDivElement | null>(null);
  const { xPos, yPos, options } = useContextMenuPosition(
    menuContainerRef as React.RefObject<HTMLDivElement>,
    inputOptions,
    position,
    boundingBox,
    timestamp,
    rootElement,
  );
  const dagContext = useDagContext();

  if (xPos <= 0 || yPos <= 0) return null;

  const style: React.CSSProperties = {
    top: `${yPos}px`,
    left: `${xPos}px`,
    // borderRadius: '4px',
    boxShadow: '3px 3px 5px -3px rgba(0, 0, 0, 0.32)',
    fontWeight: 400,
    pointerEvents: 'auto',
    position: 'absolute',
    visibility: options.length ? 'initial' : 'hidden',
  };

  const hasIcons = inputOptions.some(({ ryecon }) => ryecon !== undefined);

  return (
    <div
      style={style}
      className="select-none rounded-lg border border-borderMuted bg-bgMain text-fgMain"
      ref={menuContainerRef}
    >
      <div className="py-1">
        {inputOptions.map(({ onSelect, ...option }) => (
          <DagContextMenuItem
            key={option.label}
            {...option}
            onSelect={() => {
              onSelect?.();
              dagContext.hideContextMenu();
            }}
            showEmptyIcon={hasIcons}
          />
        ))}
      </div>
    </div>
  );
};
